import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, X, Download, LayoutGrid, ListTree, ChevronDown } from "lucide-react";
import api from "@/lib/apiClient";
import SEO from "@/components/SEO";

const PAGE = 12;

const fmtDate = (iso) => {
  const d = new Date((iso || "") + "T00:00:00");
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
};
const monthKey = (iso) => (iso || "").slice(0, 7) || "Undated";
const monthLabel = (key) => {
  if (key === "Undated") return key;
  const d = new Date(key + "-01T00:00:00");
  return isNaN(d) ? key : d.toLocaleDateString("en-GB", { month: "long", year: "numeric" }).toUpperCase();
};

async function downloadClip(c) {
  try {
    const res = await fetch(c.image);
    const blob = await res.blob();
    const ext = (blob.type.split("/")[1] || "png").split("+")[0];
    const name = `${c.outlet || "media-clip"}${c.pub_date ? `-${c.pub_date}` : ""}.${ext}`.replace(/[^\w.\-]+/g, "_");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch { window.open(c.image, "_blank"); }
}

const EMPTY_F = { q: "", outlet: "", from: "", to: "", sort: "desc" };

export default function MediaClips() {
  const [f, setF] = useState(EMPTY_F);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [view, setView] = useState("grid");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pubs, setPubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(f.q), 350);
    return () => clearTimeout(t);
  }, [f.q]);

  useEffect(() => { api.get("/media-clips/publications").then(({ data }) => setPubs(data)).catch(() => {}); }, []);

  const buildParams = useCallback((skip) => {
    const p = new URLSearchParams({ sort: f.sort, limit: String(PAGE), skip: String(skip) });
    if (debouncedQ.trim()) p.set("q", debouncedQ.trim());
    if (f.outlet) p.set("outlet", f.outlet);
    if (f.from) p.set("date_from", f.from);
    if (f.to) p.set("date_to", f.to);
    return p.toString();
  }, [f, debouncedQ]);

  const load = useCallback(async (skip, reset) => {
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const { data } = await api.get(`/media-clips?${buildParams(skip)}`);
      setTotal(data.total || 0);
      setItems((prev) => reset ? (data.items || []) : [...prev, ...(data.items || [])]);
    } catch { if (reset) { setItems([]); setTotal(0); } }
    finally { setLoading(false); setLoadingMore(false); }
  }, [buildParams]);

  useEffect(() => { load(0, true); }, [load]);

  const hasFilters = f.q || f.outlet || f.from || f.to || f.sort !== "desc";
  const clearFilters = () => { setF(EMPTY_F); setDebouncedQ(""); };
  const pickOutlet = (name) => set("outlet", f.outlet === name ? "" : name);

  const timelineGroups = items.reduce((acc, c) => {
    const k = monthKey(c.pub_date);
    (acc[k] = acc[k] || []).push(c);
    return acc;
  }, {});
  const groupOrder = Object.keys(timelineGroups).sort((a, b) => f.sort === "asc" ? a.localeCompare(b) : b.localeCompare(a));

  const inputCls = "w-full border border-npst-line rounded-md bg-white px-3 py-2.5 text-sm text-npst-ink outline-none focus:border-npst-gold";

  return (
    <div className="fade-in">
      <SEO title="Media Clips Archive — NPST" description="Searchable archive of newspaper cuttings and media clips featuring the National Public Service Trust. Filter by publication, date and keywords." canonical={`${window.location.origin}/media/clips`} />
      <section className="border-b border-npst-line paper-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Link to="/media" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-npst-muted hover:text-npst-navy mb-6" data-testid="back-to-media-link"><ArrowLeft size={13} /> Media & Communications</Link>
          <div className="label-eyebrow text-npst-gold mb-3">Media Clips</div>
          <h1 className="font-serif text-4xl sm:text-6xl font-semibold text-npst-ink tracking-tight">Media Clips Archive</h1>
          <p className="mt-4 text-npst-inkSoft max-w-2xl leading-relaxed">A permanent, searchable archive of newspaper cuttings and media clips featuring the Trust's public-interest work.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* FILTERS */}
        <div className="bg-white border border-npst-line rounded-md p-4 sm:p-5 shadow-sm" data-testid="clips-filter-bar">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="relative lg:col-span-2">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
              <input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search by newspaper, headline, keywords…"
                className={`${inputCls} pl-9`} data-testid="clips-search-input" />
            </div>
            <div className="relative">
              <select value={f.outlet} onChange={(e) => set("outlet", e.target.value)} className={`${inputCls} appearance-none pr-8 cursor-pointer`} data-testid="clips-outlet-select">
                <option value="">All Publications</option>
                {pubs.map((p) => <option key={p.outlet} value={p.outlet}>{p.outlet}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
            </div>
            <div className="relative">
              <select value={f.sort} onChange={(e) => set("sort", e.target.value)} className={`${inputCls} appearance-none pr-8 cursor-pointer`} data-testid="clips-sort-select">
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-npst-muted pointer-events-none" />
            </div>
            <div className="flex gap-2">
              <input type="date" value={f.from} onChange={(e) => set("from", e.target.value)} className={inputCls} data-testid="clips-date-from" aria-label="From date" />
              <input type="date" value={f.to} onChange={(e) => set("to", e.target.value)} className={inputCls} data-testid="clips-date-to" aria-label="To date" />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-npst-line/60">
            <div className="text-xs uppercase tracking-wider text-npst-muted" data-testid="clips-count">
              {loading ? "Loading…" : `Showing ${items.length} of ${total} clip${total !== 1 ? "s" : ""}`}
            </div>
            <div className="flex items-center gap-2">
              {hasFilters && (
                <button onClick={clearFilters} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-npst-muted hover:text-npst-navy px-3 py-2" data-testid="clips-clear-filters">
                  <X size={13} /> Clear Filters
                </button>
              )}
              <div className="flex border border-npst-line rounded-md overflow-hidden">
                <button onClick={() => setView("grid")} className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${view === "grid" ? "bg-npst-navy text-white" : "bg-white text-npst-inkSoft hover:text-npst-navy"}`} data-testid="clips-view-grid"><LayoutGrid size={13} /> Grid</button>
                <button onClick={() => setView("timeline")} className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${view === "timeline" ? "bg-npst-navy text-white" : "bg-white text-npst-inkSoft hover:text-npst-navy"}`} data-testid="clips-view-timeline"><ListTree size={13} /> Timeline</button>
              </div>
            </div>
          </div>
        </div>

        {/* BROWSE BY PUBLICATION */}
        {pubs.length > 0 && (
          <div className="mt-8" data-testid="browse-by-publication">
            <div className="label-eyebrow text-npst-gold mb-3">Browse by Publication</div>
            <div className="flex flex-wrap gap-2">
              {pubs.map((p) => (
                <button key={p.outlet} onClick={() => pickOutlet(p.outlet)}
                  className={`px-4 py-2 rounded-full border text-sm transition-colors ${f.outlet === p.outlet ? "bg-npst-navy text-white border-npst-navy" : "border-npst-line bg-white text-npst-ink hover:border-npst-gold"}`}
                  data-testid="pub-chip">
                  {p.outlet} <span className={f.outlet === p.outlet ? "text-white/70" : "text-npst-muted"}>({p.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* RESULTS */}
        <div className="mt-10">
          {loading ? <div className="py-20 text-center text-npst-muted animate-pulse">Loading archive…</div>
            : items.length === 0 ? (
              <div className="py-20 text-center" data-testid="media-clips-empty">
                <p className="font-serif text-2xl text-npst-ink">No media clips found</p>
                <p className="text-npst-muted mt-2 text-sm">Try adjusting your search or filters.</p>
              </div>
            ) : view === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="media-clips-gallery">
                {items.map((c) => (
                  <article key={c.id} className="bg-white border border-npst-line rounded-md overflow-hidden hover:border-npst-gold transition-colors flex flex-col" data-testid="clip-card">
                    <button onClick={() => downloadClip(c)} className="bg-slate-50 h-52 flex items-center justify-center p-2 cursor-pointer" aria-label="Download Newspaper Clip">
                      <img src={c.thumb || c.image} alt={c.caption || c.outlet} loading="lazy"
                        className="max-h-full max-w-full w-auto h-auto object-contain" data-testid="clip-thumb" />
                    </button>
                    <div className="p-4 flex flex-col gap-1.5 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-semibold text-npst-ink text-sm truncate">{c.outlet}</span>
                        {c.pub_date && <span className="text-[11px] uppercase tracking-wider text-npst-muted shrink-0">{fmtDate(c.pub_date)}</span>}
                      </div>
                      {c.caption && <p className="text-sm text-npst-inkSoft leading-relaxed line-clamp-2">{c.caption}</p>}
                      <button onClick={() => downloadClip(c)} className="mt-auto pt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-npst-navy hover:text-npst-gold self-start transition-colors" data-testid="view-clip-btn">
                        <Download size={13} /> View Newspaper Clip
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div data-testid="media-clips-timeline">
                {groupOrder.map((k) => (
                  <div key={k} className="mb-10" data-testid="timeline-group">
                    <div className="flex items-center gap-4 mb-4">
                      <h3 className="font-serif text-xl font-semibold text-npst-ink shrink-0">{monthLabel(k)}</h3>
                      <div className="h-px bg-npst-line flex-1" />
                      <span className="text-xs text-npst-muted uppercase tracking-wider shrink-0">{timelineGroups[k].length} clip{timelineGroups[k].length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="bg-white border border-npst-line rounded-md divide-y divide-npst-line/60">
                      {timelineGroups[k].map((c) => (
                        <div key={c.id} className="flex items-center gap-4 px-4 sm:px-5 py-3.5" data-testid="timeline-row">
                          <span className="text-[11px] uppercase tracking-wider text-npst-gold font-semibold w-24 shrink-0">{c.pub_date ? fmtDate(c.pub_date) : "—"}</span>
                          <div className="min-w-0 flex-1">
                            <span className="font-semibold text-npst-ink text-sm">{c.outlet}</span>
                            {c.caption && <span className="text-sm text-npst-inkSoft ml-2 line-clamp-1">{c.caption}</span>}
                          </div>
                          <button onClick={() => downloadClip(c)} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-npst-navy hover:text-npst-gold transition-colors" data-testid="view-clip-btn">
                            <Download size={13} /> View Clip
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

          {!loading && items.length < total && (
            <div className="mt-10 text-center">
              <button onClick={() => load(items.length, false)} disabled={loadingMore}
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-npst-navy text-npst-navy text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navy hover:text-white transition-colors disabled:opacity-50"
                data-testid="load-more-btn">
                {loadingMore ? "Loading…" : "Load More Coverage"}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
